export type BrowserZipEntryData = ArrayBuffer | ArrayBufferView | Blob | string;

export type BrowserZipEntry = {
  data: BrowserZipEntryData;
  lastModified?: Date | number;
  name: string;
};

const ZIP_UTF8_FLAG = 0x0800;
const ZIP_STORE_METHOD = 0;
const ZIP_VERSION = 20;
const MAX_ZIP_UINT16 = 0xffff;
const MAX_ZIP_UINT32 = 0xffffffff;
const CRC32_TABLE = createCrc32Table();
const zipTextEncoder = new TextEncoder();

type PreparedZipEntry = {
  crc: number;
  data: Uint8Array;
  dosDate: number;
  dosTime: number;
  name: Uint8Array;
};

export function sanitizeZipEntryName(value: string, fallback = 'arquivo'): string {
  const sanitized = value
    .replace(/[\\/:*?"<>|]+/g, '-')
    .replace(/\s+/g, ' ')
    .trim();

  return sanitized || fallback;
}

export function uniqueZipEntryName(name: string, usedNames: Set<string>): string {
  const sanitizedName = sanitizeZipEntryName(name);
  let candidate = sanitizedName;
  let suffix = 2;

  while (usedNames.has(candidate.toLowerCase())) {
    const extensionMatch = sanitizedName.match(/(\.[a-zA-Z0-9]{2,8})$/);
    if (extensionMatch) {
      const extension = extensionMatch[1];
      const baseName = sanitizedName.slice(0, -extension.length);
      candidate = `${baseName} (${suffix})${extension}`;
    } else {
      candidate = `${sanitizedName} (${suffix})`;
    }
    suffix += 1;
  }

  usedNames.add(candidate.toLowerCase());
  return candidate;
}

export async function createZipBlob(entries: BrowserZipEntry[]): Promise<Blob> {
  assertZipLength(entries.length, 'quantidade de arquivos');

  const preparedEntries = await Promise.all(entries.map(prepareZipEntry));
  const localParts: BlobPart[] = [];
  const centralParts: BlobPart[] = [];
  let offset = 0;

  for (const entry of preparedEntries) {
    assertZipSize(entry.data.byteLength, 'arquivo');
    assertZipLength(entry.name.byteLength, 'nome do arquivo');
    assertZipSize(offset, 'ZIP');

    const localHeader = createLocalFileHeader(entry);
    localParts.push(localHeader, entry.name, entry.data);

    const centralHeader = createCentralDirectoryHeader(entry, offset);
    centralParts.push(centralHeader, entry.name);

    offset += localHeader.byteLength + entry.name.byteLength + entry.data.byteLength;
  }

  const centralDirectoryOffset = offset;
  const centralDirectorySize = centralParts.reduce((total, part) => total + partSize(part), 0);
  assertZipSize(centralDirectoryOffset, 'offset do diretorio central');
  assertZipSize(centralDirectorySize, 'diretorio central');

  const endOfCentralDirectory = createEndOfCentralDirectory({
    centralDirectoryOffset,
    centralDirectorySize,
    entryCount: preparedEntries.length,
  });

  return new Blob([...localParts, ...centralParts, endOfCentralDirectory], { type: 'application/zip' });
}

export function downloadBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;
  link.download = fileName;
  link.rel = 'noopener noreferrer';
  document.body.appendChild(link);
  link.click();
  link.remove();

  window.setTimeout(() => URL.revokeObjectURL(url), 500);
}

async function prepareZipEntry(entry: BrowserZipEntry): Promise<PreparedZipEntry> {
  const data = await toUint8Array(entry.data);
  const name = zipTextEncoder.encode(sanitizeZipEntryName(entry.name));
  const { dosDate, dosTime } = toDosDateTime(entry.lastModified);

  return {
    crc: crc32(data),
    data,
    dosDate,
    dosTime,
    name,
  };
}

async function toUint8Array(data: BrowserZipEntryData): Promise<Uint8Array> {
  if (typeof data === 'string') return zipTextEncoder.encode(data);
  if (data instanceof Blob) return new Uint8Array(await data.arrayBuffer());
  if (data instanceof ArrayBuffer) return new Uint8Array(data.slice(0));
  if (ArrayBuffer.isView(data)) {
    const bytes = new Uint8Array(data.buffer, data.byteOffset, data.byteLength);
    return new Uint8Array(bytes);
  }

  throw new Error('Tipo de arquivo invalido para gerar o ZIP.');
}

function createLocalFileHeader(entry: PreparedZipEntry): Uint8Array {
  const header = new Uint8Array(30);
  const view = new DataView(header.buffer);

  view.setUint32(0, 0x04034b50, true);
  view.setUint16(4, ZIP_VERSION, true);
  view.setUint16(6, ZIP_UTF8_FLAG, true);
  view.setUint16(8, ZIP_STORE_METHOD, true);
  view.setUint16(10, entry.dosTime, true);
  view.setUint16(12, entry.dosDate, true);
  view.setUint32(14, entry.crc, true);
  view.setUint32(18, entry.data.byteLength, true);
  view.setUint32(22, entry.data.byteLength, true);
  view.setUint16(26, entry.name.byteLength, true);
  view.setUint16(28, 0, true);

  return header;
}

function createCentralDirectoryHeader(entry: PreparedZipEntry, localHeaderOffset: number): Uint8Array {
  const header = new Uint8Array(46);
  const view = new DataView(header.buffer);

  view.setUint32(0, 0x02014b50, true);
  view.setUint16(4, ZIP_VERSION, true);
  view.setUint16(6, ZIP_VERSION, true);
  view.setUint16(8, ZIP_UTF8_FLAG, true);
  view.setUint16(10, ZIP_STORE_METHOD, true);
  view.setUint16(12, entry.dosTime, true);
  view.setUint16(14, entry.dosDate, true);
  view.setUint32(16, entry.crc, true);
  view.setUint32(20, entry.data.byteLength, true);
  view.setUint32(24, entry.data.byteLength, true);
  view.setUint16(28, entry.name.byteLength, true);
  view.setUint16(30, 0, true);
  view.setUint16(32, 0, true);
  view.setUint16(34, 0, true);
  view.setUint16(36, 0, true);
  view.setUint32(38, 0, true);
  view.setUint32(42, localHeaderOffset, true);

  return header;
}

function createEndOfCentralDirectory({
  centralDirectoryOffset,
  centralDirectorySize,
  entryCount,
}: {
  centralDirectoryOffset: number;
  centralDirectorySize: number;
  entryCount: number;
}): Uint8Array {
  const header = new Uint8Array(22);
  const view = new DataView(header.buffer);

  view.setUint32(0, 0x06054b50, true);
  view.setUint16(4, 0, true);
  view.setUint16(6, 0, true);
  view.setUint16(8, entryCount, true);
  view.setUint16(10, entryCount, true);
  view.setUint32(12, centralDirectorySize, true);
  view.setUint32(16, centralDirectoryOffset, true);
  view.setUint16(20, 0, true);

  return header;
}

function createCrc32Table(): Uint32Array {
  const table = new Uint32Array(256);

  for (let index = 0; index < table.length; index += 1) {
    let value = index;
    for (let bit = 0; bit < 8; bit += 1) {
      value = value & 1 ? 0xedb88320 ^ (value >>> 1) : value >>> 1;
    }
    table[index] = value >>> 0;
  }

  return table;
}

function crc32(data: Uint8Array): number {
  let crc = 0xffffffff;

  for (const byte of data) {
    crc = CRC32_TABLE[(crc ^ byte) & 0xff] ^ (crc >>> 8);
  }

  return (crc ^ 0xffffffff) >>> 0;
}

function toDosDateTime(value?: Date | number): { dosDate: number; dosTime: number } {
  const date = value instanceof Date ? value : value ? new Date(value) : new Date();
  const safeDate = date.getFullYear() < 1980 ? new Date(1980, 0, 1, 0, 0, 0) : date;
  const year = Math.min(safeDate.getFullYear(), 2107);
  const month = safeDate.getMonth() + 1;
  const day = safeDate.getDate();
  const hours = safeDate.getHours();
  const minutes = safeDate.getMinutes();
  const seconds = Math.floor(safeDate.getSeconds() / 2);

  return {
    dosDate: ((year - 1980) << 9) | (month << 5) | day,
    dosTime: (hours << 11) | (minutes << 5) | seconds,
  };
}

function partSize(part: BlobPart): number {
  if (typeof part === 'string') return zipTextEncoder.encode(part).byteLength;
  if (part instanceof Blob) return part.size;
  if (part instanceof ArrayBuffer) return part.byteLength;
  return part.byteLength;
}

function assertZipSize(value: number, label: string): void {
  if (value > MAX_ZIP_UINT32) {
    throw new Error(`O ${label} excede o limite de tamanho suportado para ZIP local.`);
  }
}

function assertZipLength(value: number, label: string): void {
  if (value > MAX_ZIP_UINT16) {
    throw new Error(`O ${label} excede o limite suportado para ZIP local.`);
  }
}
